import countryRawList from 'react-select-country-list';
import moment from 'moment-timezone';
import deburr from 'lodash/deburr';
import T from 'i18n-react';
import { DATE_FORMAT_01 } from 'shared/constants';
import configureI18n from 'helpers/i18n';
import Language from './Language';

const year = new Date().getFullYear();

export const ApiPrefix = process.env.REACT_APP_API_PREFIX;
export const ApiPrefixV1 = process.env.REACT_APP_API_PREFIX_V1;
export const ApiV1Base = process.env.REACT_APP_API_BASE_V1_URL || 'https://dev-api.icw.io';
export const ApiBase = process.env.REACT_APP_API_BASE_URL || 'https://qmas.icw.io';

export function statusCheck(response) {
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }
  const err = new Error(response.statusText);
  err.status = response.status;
  return Promise.reject(err);
}
export function checkToGetErrorResponse(response) {
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }
  return Promise.reject(response);
}

export function json(response) {
  return response.json();
}

export function blob(response) {
  return response.blob();
}

export function qs(params) {
  return `?${Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&')}`;
}

export function validEmail(email) {
  // eslint-disable-next-line max-len
  const re = /^[-a-z0-9~!$%^&*_=+}{'?]+(\.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i;
  return re.test(email);
}

export function isProductionEnv() {
  const WEB_ENV = typeof process !== 'undefined' && process.env && process.env.REACT_APP_WEB_ENV;
  return WEB_ENV === 'production';
}

export function setDocumentCookie(data) {
  document.cookie = `${data};path=/${isProductionEnv() ? ';secure' : ''}`;
}

export function setCookie(cname, value, exdays = 30) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  setDocumentCookie(`${cname}=${value};${expires}`);
}

export function getCookie(cname) {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

// For icw1, get cookie.locale
// For icw2, need to change to use loginState.profile.locale
export function getLocale() {
  return getCookie('locale') || 'en';
}

export function getLocaleForCategories() {
  const locale = getCookie('locale') || 'en';

  switch (locale) {
    case 'zh-hk':
      return 'zhhk';
    case 'zh-cn':
      return 'zhcn';
    default:
      return 'en';
  }
}

export function filterLocale(lang) {
  switch (lang.toLowerCase().match(/^[^(,|;)]*/i)[0]) {
    case 'zh':
    case 'zh-hk':
    case 'zh-hant':
    case 'zh-tw':
    case 'zh-sg':
    case 'yue-hk':
      return 'zh-hk';
    case 'zh-cn':
    case 'zh-hans':
      return 'zh-cn';
    case 'fr':
      return 'fr';
    case 'it':
      return 'it';
    case 'en':
    case 'en-us':
    case 'en-gb':
      return 'en';
    default:
      return null;
  }
}

export function setLanguageLocale(T, langPath) {
  const pathname = window.location.pathname || '';
  const lang = filterLocale(pathname.split('/')[1]);
  const locale = lang || getLocale();
  const language = new Language(locale, langPath);
  setCookie('locale', locale);
  configureI18n(locale);

  // T.setTexts({ ...T.texts, ...language.getDictionary() }, { MDFlavor: 1 });
}

export function getURLParams() {
  const vars = {};
  window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
    vars[key] = value;
  });
  return vars;
}

export function formatDate(date, format = 'YYYY-MM-DD') {
  if (moment(date, format).isValid()) {
    return moment(date)
      .tz(moment.tz.guess())
      .format(format);
  }

  return date;
}

export function formatDateMoment(date, format = DATE_FORMAT_01) {
  return moment(date).format(format);
}

export function formatCost(item, currency = 'usd') {
  const CURRENCY = currency.toUpperCase();
  const itemCost = Number(item);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const result = itemCost === 0 ? '-' : formatter.format(itemCost).replace('$', '');
  return [CURRENCY, result].join(' ').trim();
}

export function productSum(unit, factor, sum) {
  return Number(unit * factor + sum).toFixed(2);
}

export const countryList = countryRawList()
  .getData()
  .filter(
    (rec) =>
      !(rec.label === 'Hong Kong' || rec.label === 'Macao' || rec.label === 'Virgin Islands, U.S.')
  )
  .map((country) => ({ region: deburr(country.label) }));

export const countryListWithoutVIUS = countryRawList()
  .getData()
  .filter((rec) => !(rec.label === 'Virgin Islands, U.S.'))
  .map((country) => ({ region: deburr(country.label) }));

export function inspectionReportStatus(data) {
  const itemStatus = data.inspectionPOReportGroupMaps
    .map((report) =>
      report.inspectionPurchaseOrder.inspectionPOItems.map((item) =>
        item.inspectionDefectGroup ? item.inspectionDefectGroup.status : undefined
      )
    )
    .reduce((acc, val) => acc.concat(val), []);

  if (itemStatus.some((status) => status === 'failed')) return 'failed';

  if (itemStatus.every((status) => status === 'passed')) return 'passed';

  if (itemStatus.every((status) => status === 'productInVain')) return 'productInVain';

  return 'pending';
}

export const nextPath = (history) => (path, state = null) => history.push(path, state);
export const nextPath2 = (path) => {
  window.location.href = path;
};
export const nextPath3 = (history) => (path, state = null) =>
  history.push({
    pathname: path,
    state,
  });

export const redirectAdminPath = (history) => {
  if (!window.location.pathname.includes('/admin')) {
    nextPath(history)(`/admin${window.location.pathname}`);
  }
};

export const valueOrUnknown = (value, unknownText = T.translate('general.na')) => {
  return value || unknownText;
};
export const valueWithZeroOrUnknown = (value, unknownText = T.translate('general.na')) => {
  if (value === 0) {
    return value;
  }
  return value || unknownText;
};
export const valueOrEmptyString = (value, emptyText = '') => {
  return value || emptyText;
};

export const capitalizeFirstLetter = (string = '') => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const stringToBoolean = (string = '') => {
  switch (string.toLowerCase()) {
    case 'false':
    case 'no':
    case '0':
    case '':
      return false;
    default:
      return true;
  }
};

export const scrollToTop = () => {
  window.scrollTo(0, 0);
};

export const defectReason = [
  {
    label: 'workmanship',
    value: 'Workmanship',
  },
  {
    label: 'on-site-tests',
    value: 'On-Site Tests',
  },
  {
    label: 'labeling-printing-marking',
    value: 'Labeling/Printing/Marking',
  },
  {
    label: 'shipping-marks',
    value: 'Shipping Marks',
  },
  {
    label: 'packing',
    value: 'Packing',
  },
  {
    label: 'sample-card-swatch',
    value: 'Submitted Samples/Trim card/Color swatch',
  },
  {
    label: 'product-specification',
    value: 'Product Specifications/Drawings',
  },
  {
    label: 'product-information',
    value: 'Product Information on PO',
  },
  {
    label: 'assortment',
    value: 'Assortment',
  },
  {
    label: 'barcode-verification',
    value: 'Barcode Verification',
  },
  {
    label: 'measurement-result',
    value: 'Measurement Result',
  },
  {
    label: 'weight-result',
    value: 'Weight Result',
  },
  {
    label: 'inspection-conditions',
    value: 'Inspection Conditions',
  },
  {
    label: 'program-specific-requirements',
    value: 'Program Specific Requirements',
  },
  {
    label: 'condition-of-container',
    value: 'Condition of Container(s)',
  },
];

export const topMarketList = [{ region: 'Europe' }, ...countryList];

export const yearList = Array.from(new Array(200), (val, index) => (year - index).toString());

export const provinceList = [
  'Hong Kong',
  'Macau',
  'Beijing',
  'Shanghai',
  'Anhui',
  'Chongqing',
  'Fujian',
  'Gansu',
  'Guangdong',
  'Guangxi',
  'Guizhou',
  'Hebei',
  'Heilongjiang',
  'Henan',
  'Hubei',
  'Hunan',
  'InnerMongolia',
  'Jiangsu',
  'Jiangxi',
  'Jilin',
  'Liaoning',
  'Ningxia',
  'Qinghai',
  'Shaanxi',
  'Shandong',
  'Shanxi',
  'Sichuan',
  'Tianjin',
  'Tibet',
  'Xinjiang',
  'Yunnan',
  'Zhejiang',
];

export const stateList = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District Of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

export const businessType = [
  { label: T.translate('businessType.retailers'), value: 'retailers' },
  { label: T.translate('businessType.distributor'), value: 'distributor' },
  { label: T.translate('businessType.tradingCompany'), value: 'tradingCompany' },
  { label: T.translate('businessType.manufacturers'), value: 'manufacturers' },
  { label: T.translate('businessType.factory'), value: 'factory' },
  { label: T.translate('businessType.rawMaterial'), value: 'rawMaterial' },
];

export const paymentTerms = [
  { label: 'T/T', value: 'T/T' },
  { label: 'L/C', value: 'L/C' },
  { label: 'D/P', value: 'D/P' },
  { label: 'Western Union', value: 'Western Union' },
  { label: 'Money Gram', value: 'Money Gram' },
];

export const tradeTerms = [
  { label: 'FOB', value: 'FOB' },
  { label: 'EXW', value: 'EXW' },
  { label: 'FAS', value: 'FAS' },
  { label: 'FCA', value: 'FCA' },
  { label: 'CFR', value: 'CFR' },
  { label: 'CPT', value: 'CPT' },
  { label: 'CIF', value: 'CIF' },
  { label: 'CIP', value: 'CIP' },
  { label: 'DES', value: 'DES' },
  { label: 'DAF', value: 'DAF' },
  { label: 'DEQ', value: 'DEQ' },
  { label: 'DDP', value: 'DDP' },
  { label: 'DDU', value: 'DDU' },
];

export const getExpiryDateValidation = (date, number) => {
  const result = date;
  result.setDate(date.getDate() - 1);
  result.setFullYear(date.getFullYear() + number);

  return result;
};

export const getOrdinalSuffixOf = (i) => {
  const j = i % 10;
  const k = i % 100;

  if (j === 1 && k !== 11) {
    return `${i}st`;
  }
  if (j === 2 && k !== 12) {
    return `${i}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${i}rd`;
  }
  return `${i}th`;
};

export const getTranslatedArray = (arr = []) => {
  return arr.map((item) => ({ ...item, label: T.translate(item.label) }));
};

export const hexToRGB = (hex, alpha) => {
  if (!hex) return '';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
};

export const urlify = (text) => {
  const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g;
  const message = (text || '').replace(urlRegex, (url) => {
    let hyperlink = url;
    if (!hyperlink.match('^https?://')) {
      hyperlink = `http://${hyperlink}`;
    }
    return `<a href="${hyperlink}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });

  return message;
};
